import { useId, type CSSProperties } from 'react';
import { BarChart3 } from 'lucide-react';
import { cn } from '@agenda-facil/ui';
import type { ChartPoint } from './dashboard-insights';

interface ChartProps {
  points: ChartPoint[];
  ariaLabel: string;
  valueFormatter?: (value: number) => string;
}

function coordinates(points: ChartPoint[], width: number, height: number, padding: number) {
  const max = Math.max(...points.map((point) => point.value), 1);
  return points.map((point, index) => ({
    ...point,
    x:
      points.length === 1
        ? width / 2
        : padding + (index / (points.length - 1)) * (width - padding * 2),
    y: height - padding - (point.value / max) * (height - padding * 2),
  }));
}

function smoothPath(points: Array<{ x: number; y: number }>): string {
  const first = points[0];
  if (!first) return '';
  return points.slice(1).reduce((path, point, index) => {
    const previous = points[index] ?? first;
    const midpoint = (previous.x + point.x) / 2;
    return `${path} C ${midpoint} ${previous.y}, ${midpoint} ${point.y}, ${point.x} ${point.y}`;
  }, `M ${first.x} ${first.y}`);
}

export function MetricSparkline({
  points,
  className,
}: {
  points: ChartPoint[];
  className?: string;
}) {
  if (points.length === 0 || points.every((point) => point.value === 0)) {
    return <span className="h-7 w-20 rounded-lg bg-[var(--surface-raised)]" aria-hidden="true" />;
  }
  const plotted = coordinates(points, 84, 30, 3);
  return (
    <svg
      viewBox="0 0 84 30"
      className={cn('h-7 w-20 overflow-visible text-[var(--accent)]', className)}
      aria-hidden="true"
    >
      <path
        d={smoothPath(plotted)}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        className="motion-chart-line"
        pathLength="1"
      />
    </svg>
  );
}

export function RevenueAreaChart({ points, ariaLabel, valueFormatter }: ChartProps) {
  const gradientId = `revenue-area-${useId().replaceAll(':', '')}`;
  if (points.length === 0 || points.every((point) => point.value === 0)) {
    return <ChartEmptyState message="O faturamento aparecerá quando houver vendas concluídas." />;
  }

  const width = 640;
  const height = 230;
  const padding = 36;
  const plotted = coordinates(points, width, height, padding);
  const line = smoothPath(plotted);
  const first = plotted[0];
  const last = plotted.at(-1);
  const baseline = height - padding;
  const area = first && last ? `${line} L ${last.x} ${baseline} L ${first.x} ${baseline} Z` : '';
  const maxValue = Math.max(...points.map((point) => point.value), 1);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-auto w-full overflow-visible"
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.01" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75, 1].map((ratio) => (
        <line
          key={ratio}
          x1={padding}
          x2={width - padding}
          y1={baseline - ratio * (height - padding * 2)}
          y2={baseline - ratio * (height - padding * 2)}
          stroke="var(--border)"
          strokeWidth="1"
        />
      ))}
      <path d={area} fill={`url(#${gradientId})`} className="motion-chart-area" />
      <path
        d={line}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="3"
        strokeLinecap="round"
        pathLength="1"
        className="motion-chart-line"
      />
      {plotted.map((point) => (
        <g key={point.label}>
          <circle
            cx={point.x}
            cy={point.y}
            r="4"
            fill="var(--surface)"
            stroke="var(--accent)"
            strokeWidth="2.5"
          >
            <title>{`${point.label}: ${valueFormatter ? valueFormatter(point.value) : point.value}`}</title>
          </circle>
          <text
            x={point.x}
            y={height - 8}
            textAnchor="middle"
            fontSize="11"
            fontWeight="750"
            fill="var(--muted-foreground)"
          >
            {point.label}
          </text>
        </g>
      ))}
      <text x={padding} y={18} fontSize="10" fontWeight="700" fill="var(--muted-foreground)">
        {valueFormatter ? valueFormatter(maxValue) : maxValue}
      </text>
    </svg>
  );
}

export function RoundedBarChart({ points, ariaLabel, valueFormatter }: ChartProps) {
  if (points.length === 0 || points.every((point) => point.value === 0)) {
    return (
      <ChartEmptyState message="Os atendimentos aparecerão quando a agenda receber reservas." />
    );
  }
  const max = Math.max(...points.map((point) => point.value), 1);
  return (
    <div
      className="grid h-52 grid-cols-[repeat(var(--chart-count),minmax(34px,1fr))] items-end gap-2 pt-7 sm:gap-3"
      style={{ '--chart-count': points.length } as CSSProperties}
      role="img"
      aria-label={`${ariaLabel}. ${points
        .map(
          (point) =>
            `${point.label}: ${valueFormatter ? valueFormatter(point.value) : point.value}`,
        )
        .join(', ')}`}
    >
      {points.map((point, index) => {
        const height = Math.max(point.value > 0 ? 10 : 2, (point.value / max) * 100);
        return (
          <div key={point.label} className="flex h-full min-w-0 flex-col justify-end gap-2">
            <span className="text-center text-[10px] font-black tabular-nums text-[var(--foreground)]">
              {valueFormatter ? valueFormatter(point.value) : point.value}
            </span>
            <div className="flex min-h-0 flex-1 items-end rounded-xl bg-[var(--surface-raised)]">
              <span
                className="motion-bar-in block w-full origin-bottom rounded-xl bg-[var(--brand)]"
                style={{ height: `${height}%`, animationDelay: `${Math.min(index * 35, 180)}ms` }}
              />
            </div>
            <span className="truncate text-center text-[10px] font-extrabold uppercase text-[var(--muted-foreground)]">
              {point.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function PeakHoursChart({ points }: { points: ChartPoint[] }) {
  if (points.length === 0) {
    return (
      <ChartEmptyState message="Ainda não há atendimentos suficientes para identificar horários de pico." />
    );
  }
  const max = Math.max(...points.map((point) => point.value), 1);
  return (
    <div className="space-y-4" role="list" aria-label="Horários de maior movimento">
      {points.map((point, index) => (
        <div
          key={point.label}
          className="grid grid-cols-[42px_1fr_auto] items-center gap-3"
          role="listitem"
        >
          <span className="text-xs font-black tabular-nums text-[var(--foreground)]">
            {point.label}
          </span>
          <span className="h-2.5 overflow-hidden rounded-full bg-[var(--surface-raised)]">
            <span
              className="motion-progress-in block h-full origin-left rounded-full bg-[var(--accent)]"
              style={{
                width: `${(point.value / max) * 100}%`,
                animationDelay: `${Math.min(index * 45, 180)}ms`,
              }}
            />
          </span>
          <span className="text-[11px] font-extrabold text-[var(--muted-foreground)]">
            {point.value} {point.value === 1 ? 'atendimento' : 'atendimentos'}
          </span>
        </div>
      ))}
    </div>
  );
}

export function OccupancyRing({ percentage }: { percentage: number }) {
  const circumference = 2 * Math.PI * 38;
  const offset = circumference - (circumference * percentage) / 100;
  return (
    <div className="relative grid size-36 shrink-0 place-items-center">
      <svg className="size-36 -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
        <circle
          cx="50"
          cy="50"
          r="38"
          fill="transparent"
          stroke="var(--surface-raised)"
          strokeWidth="9"
        />
        <circle
          cx="50"
          cy="50"
          r="38"
          fill="transparent"
          stroke="var(--brand)"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="motion-ring-in"
        />
      </svg>
      <div className="absolute text-center">
        <strong className="text-2xl font-black tabular-nums text-[var(--foreground)]">
          {percentage}%
        </strong>
        <span className="block text-[9px] font-black uppercase tracking-[0.12em] text-[var(--muted-foreground)]">
          Ocupado
        </span>
      </div>
    </div>
  );
}

export function ChartEmptyState({ message }: { message: string }) {
  return (
    <div className="grid min-h-48 place-items-center rounded-2xl border border-dashed border-[var(--border)] bg-[var(--input)] p-6 text-center">
      <div className="max-w-xs">
        <BarChart3
          className="mx-auto text-[var(--muted-foreground)]"
          size={22}
          aria-hidden="true"
        />
        <p className="mt-3 text-xs leading-5 text-[var(--muted-foreground)]">{message}</p>
      </div>
    </div>
  );
}
